/*
 * Copyright (C) 2026 Nex4Nova (Geoblast group)
 * SPDX-License-Identifier: Proprietary
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { createPortal } from 'react-dom';
import {
  TracePlot,
  useDrillholeTraceGrid,
  BASELODE_DARK_TEMPLATE,
  BASELODE_LIGHT_TEMPLATE,
} from 'baselode';
import './Drillhole2D.css';
import { useProjectData } from '../../contexts/ProjectDataContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { useStripLogSelections } from '../../contexts/StripLogSelectionsContext.jsx';
import { useLanguage } from '../../contexts/LanguageContext.jsx';
import { buildCategoricalColorMap } from '../../lib/categoricalColors.js';

const PLOT_COUNT_KEY = 'nex4nova-drilling-strip-log-plot-count-v1';
const PANEL_CONFIG_KEY = 'nex4nova-drilling-strip-log-panels-v1';
const PLOT_COUNT_MIN = 1;
const PLOT_COUNT_MAX = 16;
// 6 default panels show the full demo mineralisation story side by side:
// Cu, Au, Ag, Cr concentrations, lithology and structure dip, all aligned
// by depth — the "parallel analytics" view geologists expect on first load.
const PLOT_COUNT_DEFAULT = 6;

// Order matters: the seed picks the first N available properties. Copper
// first (the flagship metal), then Au/Ag/Cr, geology and structure dip.
const PREFERRED_PROPS = ['Cu_PCT', 'Cu', 'Au_PPM', 'Au', 'Ag_PPM', 'Cr_PPM', 'geology_code', 'lithology', 'dip', 'rock_type', 'Ni_PPM', 'Co_PPM'];

function clampPlotCount(n) {
  const v = Math.floor(Number(n));
  if (!Number.isFinite(v)) return PLOT_COUNT_DEFAULT;
  return Math.min(PLOT_COUNT_MAX, Math.max(PLOT_COUNT_MIN, v));
}

function readInitialPlotCount() {
  try {
    const v = localStorage.getItem(PLOT_COUNT_KEY);
    if (v != null) return clampPlotCount(v);
  } catch (e) { /* ignore */ }
  return PLOT_COUNT_DEFAULT;
}

function Drillhole2D() {
  const location = useLocation();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const { collars, combinedHoles, geologyHoles, rawCsv, status } = useProjectData();
  const {
    selections: stripCache,
    setAllConfigs: setStripCache,
    setPanelPatch: setStripPanel,
  } = useStripLogSelections();

  // Both baselode templates (dark and the unnamed default used when no
  // template is passed) set hovermode: 'x unified', which makes categorical
  // strip logs (every text point at x=0.5) collapse into a single stacked
  // tooltip listing all intervals. Strip logs are y-axis (depth) oriented,
  // so 'closest' reads correctly for both categorical and numeric tracks.
  const template = useMemo(() => {
    const base = theme === 'dark' ? BASELODE_DARK_TEMPLATE : BASELODE_LIGHT_TEMPLATE;
    return {
      ...base,
      layout: { ...base.layout, hovermode: 'closest' },
    };
  }, [theme]);

  const [plotCount, setPlotCount] = useState(readInitialPlotCount);
  const [alignDepthAxes, setAlignDepthAxes] = useState(false);
  const [alignDepthRange, setAlignDepthRange] = useState(false);
  const [rangeStart, setRangeStart] = useState('0');
  const [rangeEnd, setRangeEnd] = useState('1000');
  useEffect(() => {
    try { localStorage.setItem(PLOT_COUNT_KEY, String(plotCount)); } catch (e) { /* ignore */ }
  }, [plotCount]);

  // The hook's dropdown options are built only from holes in its internal
  // state, which is seeded from `extraHoles`. Pass a merged list: real
  // intervaled holes first (they have points), then stub entries for every
  // collar so all hole IDs appear in the picker even when they have no
  // downhole data.
  const extraHoles = useMemo(() => {
    const known = new Set((combinedHoles || []).map((h) => h?.id || h?.holeId).filter(Boolean));
    const stubs = (collars || [])
      .map((c) => c?.holeId)
      .filter((id) => id && !known.has(id))
      .map((id) => ({ id, holeId: id, points: [] }));
    return [...(combinedHoles || []), ...stubs];
  }, [collars, combinedHoles]);

  const {
    error,
    setError,
    holeCount,
    setFocusedHoleId,
    labeledHoleOptions,
    traceGraphs,
    handleConfigChange,
  } = useDrillholeTraceGrid({
    initialFocusedHoleId: location.state?.holeId || '',
    extraHoles,
    plotCount,
  });

  // Enrich the hook's flat hole list with a `project` field so the
  // TracePlot's `group+hole` selector can offer a project filter.  An
  // empty / missing collar.project resolves to '' — the TracePlot
  // renders it as a "No projects" option and the hole list stays
  // unfiltered, which is the "no project code → just (none)" case.
  const holeToProject = useMemo(() => {
    const map = new Map();
    for (const collar of collars || []) {
      if (!collar?.holeId) continue;
      const project = (collar.project ?? '').toString().trim();
      if (project) map.set(collar.holeId, project);
    }
    return map;
  }, [collars]);

  const holeOptionsWithProject = useMemo(
    () => labeledHoleOptions.map((option) => ({
      ...option,
      project: holeToProject.get(option.holeId) || '',
    })).sort((a, b) => String(a.label || a.holeId).localeCompare(
      String(b.label || b.holeId), undefined, { sensitivity: 'base' }
    )),
    [labeledHoleOptions, holeToProject],
  );

  const projectOptions = useMemo(() => {
    const seen = new Set();
    for (const option of holeOptionsWithProject) {
      if (option.project) seen.add(option.project);
    }
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [holeOptionsWithProject]);

  // When the data carries exactly one project — and every hole belongs to
  // it — default each panel's Project filter to that project so the
  // dropdown shows it instead of sitting on the "Select project"
  // placeholder. Guarded on every hole matching so auto-filtering can
  // never hide a hole (an unassigned-project hole would otherwise vanish
  // from the picker, blanking a panel).
  const soleProject = useMemo(() => (
    projectOptions.length === 1
      && holeOptionsWithProject.every((option) => option.project === projectOptions[0])
      ? projectOptions[0]
      : ''
  ), [projectOptions, holeOptionsWithProject]);

  useEffect(() => {
    const holeIdFromNav = location.state?.holeId;
    if (holeIdFromNav) {
      setFocusedHoleId(holeIdFromNav);
    }
    // "Loading hole X" is only valid while the project is still being
    // parsed. Once status is 'ready' the grid state is final — never leave
    // the loading banner stuck (seen on Firefox when holeCount stays 0).
    if (holeIdFromNav && !holeCount && status !== 'ready') {
      setError((prev) => prev || t('strip.loadingHole', { id: holeIdFromNav }));
    } else {
      setError('');
    }
  }, [location.state, holeCount, status, setError, setFocusedHoleId]);

  // Persisted configs: load from localStorage on mount.
  const persistedRestored = useRef(false);
  useEffect(() => {
    if (persistedRestored.current) return;
    persistedRestored.current = true;
    try {
      const raw = localStorage.getItem(PANEL_CONFIG_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (Array.isArray(saved) && saved.length > 0) setStripCache(saved);
      }
    } catch (e) { /* ignore */ }
  }, [setStripCache]);

  // Restore cached per-panel configs once the hook has primed its
  // defaults.  Only runs once per mount — subsequent panel edits flow
  // through the mirror effect below into the cache, not the other way.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    if (!traceGraphs.length) return;
    if (!traceGraphs.every((graph) => graph?.config?.holeId)) return;
    (stripCache.configs || []).forEach((cached, idx) => {
      if (!cached || idx >= traceGraphs.length) return;
      const current = traceGraphs[idx]?.config || {};
      const patch = {};
      if (cached.holeId && cached.holeId !== current.holeId) patch.holeId = cached.holeId;
      if (cached.property && cached.property !== current.property) patch.property = cached.property;
      if (cached.chartType && cached.chartType !== current.chartType) patch.chartType = cached.chartType;
      if (Object.keys(patch).length) handleConfigChange(idx, patch);
    });
    restoredRef.current = true;
  }, [traceGraphs, stripCache.configs, handleConfigChange]);

  // Mirror live per-panel configs into the cache so they survive a
  // navigation away and back.  Also persists to localStorage.
  useEffect(() => {
    if (!restoredRef.current) return;
    if (!traceGraphs.length) return;
    const next = traceGraphs.map((graph) => {
      const config = graph?.config;
      if (!config) return null;
      return { holeId: config.holeId, property: config.property, chartType: config.chartType };
    });
    setStripCache(next);
    try { localStorage.setItem(PANEL_CONFIG_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ }
  }, [traceGraphs, setStripCache]);

  // Seed panels with preferred properties when data is first ready and
  // no persisted config exists.  Picks in order: Au, Cu, geology, dip
  // (from PREFERRED_PROPS) then fills remaining with other available
  // properties.  Only runs once per mount.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (status !== 'ready') return;
    if (!traceGraphs.length) return;

    // If configs were restored from localStorage, skip seeding.
    if (stripCache.configs?.some((c) => c?.holeId)) {
      seededRef.current = true;
      return;
    }

    const sourceProps = traceGraphs[0]?.propertyOptions || [];
    if (!sourceProps.length) return;

    const getPropValue = (opt) => (typeof opt === 'string' ? opt : (opt?.value || ''));
    const allProps = sourceProps.map(getPropValue).filter(Boolean);
    if (!allProps.length) return;

    const targetHoleId = location.state?.holeId || labeledHoleOptions[0]?.holeId || '';
    if (!targetHoleId) return;

    // Build ordered list: preferred props first (case-insensitive —
    // baselode emits property ids in lowercase), then remaining in source
    // order. The mix (assays + geology + structure) keeps the strip log
    // illustrative: numeric traces, categorical intervals and dip tadpoles.
    const normalizeProp = (value) => String(value || '').toLowerCase();
    const preferredSet = new Set(PREFERRED_PROPS.map(normalizeProp));
    const ordered = [];
    const remainder = [];
    for (const prop of allProps) {
      if (preferredSet.has(normalizeProp(prop))) ordered.push(prop);
      else remainder.push(prop);
    }
    const sorted = [...ordered, ...remainder];

    const projectId = soleProject;
    let changed = false;
    sorted.slice(0, plotCount).forEach((prop, idx) => {
      const current = traceGraphs[idx]?.config;
      if (!current) return;
      const patch = {};
      if (current.holeId !== targetHoleId) patch.holeId = targetHoleId;
      if (current.property !== prop) patch.property = prop;
      if (Object.keys(patch).length) {
        handleConfigChange(idx, patch);
        changed = true;
      }
      if (projectId) setStripPanel(idx, { projectId });
    });

    if (changed) seededRef.current = true;
  }, [status, traceGraphs, labeledHoleOptions, location.state, handleConfigChange, plotCount, stripCache.configs]);

  const dataSourceTarget = typeof document !== 'undefined' ? document.getElementById('data-source-slot') : null;
  const dataSourceInfo = (
    <div className="data-source-text">
      {holeCount > 0 && <div>{t('strip.holeCount', { n: holeCount })}</div>}
    </div>
  );

  const controlsTarget = typeof document !== 'undefined' ? document.getElementById('strip-log-controls-slot') : null;
  const firstPanelHoleId = traceGraphs[0]?.config?.holeId || '';
  const canApplyFirstHole = plotCount >= 2 && Boolean(firstPanelHoleId);
  const parsedDepthRange = useMemo(() => {
    const from = Number(rangeStart);
    const to = Number(rangeEnd);
    return Number.isFinite(from) && Number.isFinite(to) && from >= 0 && to > from ? [from, to] : null;
  }, [rangeStart, rangeEnd]);
  const applyDepthRange = (enabled, range = parsedDepthRange) => {
    if (enabled && !range) return;
    setAlignDepthRange(enabled);
    Array.from({ length: plotCount }).forEach((_, index) => {
      setStripPanel(index, { depthRange: enabled ? range : null, ...(enabled ? { startFromZero: false } : {}) });
    });
  };
  useEffect(() => {
    if (!alignDepthRange || !parsedDepthRange) return;
    Array.from({ length: plotCount }).forEach((_, index) => {
      setStripPanel(index, { depthRange: parsedDepthRange, startFromZero: false });
    });
  }, [alignDepthRange, parsedDepthRange, plotCount, setStripPanel]);
  const sidebarControls = (
    <div className="strip-log-controls">
      <div className="label-caps">{t('strip.title')}</div>
      <label className="strip-log-control">
        <span>{t('strip.plots')}</span>
        <input
          type="number"
          min={PLOT_COUNT_MIN}
          max={PLOT_COUNT_MAX}
          step={1}
          value={plotCount}
          onChange={(e) => setPlotCount(clampPlotCount(e.target.value))}
        />
      </label>
      <button
        type="button"
        className="primary-button strip-log-apply-hole"
        disabled={!canApplyFirstHole}
        title={canApplyFirstHole ? t('strip.applyTooltip') : t('strip.applyDisabled')}
        onClick={() => {
          traceGraphs.forEach((graph, index) => {
            if (index > 0 && graph?.config?.holeId !== firstPanelHoleId) handleConfigChange(index, { holeId: firstPanelHoleId });
          });
        }}
      >
        {t('strip.applyBtn')}
      </button>
      <label className="strip-log-control strip-log-checkbox">
        <input
          type="checkbox"
          checked={alignDepthAxes}
          onChange={(event) => {
            const next = event.target.checked;
            setAlignDepthAxes(next);
            if (next) applyDepthRange(false);
            Array.from({ length: plotCount }).forEach((_, index) => setStripPanel(index, { startFromZero: next }));
          }}
        />
        <span>{t('strip.startAtZero')}</span>
      </label>
      <label className="strip-log-control strip-log-checkbox">
        <input
          type="checkbox"
          checked={alignDepthRange}
          disabled={!parsedDepthRange}
          onChange={(event) => {
            const next = event.target.checked;
            if (next) setAlignDepthAxes(false);
            applyDepthRange(next);
          }}
        />
        <span>{t('strip.alignDepth')}</span>
      </label>
      <div className="strip-log-depth-range" aria-label={t('strip.depthRange')}>
        <label>
          <span>{t('strip.from')}</span>
          <input type="number" min="0" step="any" value={rangeStart} onChange={(event) => setRangeStart(event.target.value)} />
        </label>
        <label>
          <span>{t('strip.to')}</span>
          <input type="number" min="0" step="any" value={rangeEnd} onChange={(event) => setRangeEnd(event.target.value)} />
        </label>
      </div>
      {!parsedDepthRange && <div className="strip-log-range-error">{t('strip.rangeError')}</div>}
    </div>
  );

  return (
    <div className="drillhole2d-container">
      {error && (
        <div className="drillhole2d-header">
          <div className="drillhole2d-controls">
            <span className="error-text">{error}</span>
          </div>
        </div>
      )}

      {status !== 'ready' ? (
        <div className="page-empty-state">
          <h2>{t('strip.emptyTitle')}</h2>
          <p>{t('strip.emptyDesc')}</p>
        </div>
      ) : (
        <div className="plots-grid">
          {Array.from({ length: plotCount }).map((_, idx) => {
            const panelCache = stripCache.configs[idx] || {};
            const projectId = panelCache.projectId || soleProject;
            const graph = traceGraphs[idx];
            const config = {
              ...(graph?.config || { holeId: '', property: '', chartType: 'markers+line' }),
              logScale: panelCache.logScale === true,
              usePatterns: panelCache.usePatterns === true,
              stepped: panelCache.stepped === true,
              fillArea: panelCache.fillArea === true,
              startFromZero: panelCache.startFromZero === true,
              depthRange: panelCache.depthRange || null,
            };
            return (
              <TracePlot
                key={idx}
                config={config}
                graph={graph}
                holeOptions={holeOptionsWithProject}
                holeSelector={{
                  kind: 'group+hole',
                  groupBy: 'project',
                  groupLabel: t('strip.projectLabel'),
                  groupValue: projectId,
                  groupOptions: projectOptions,
                  onGroupChange: (nextProject) => {
                    setStripPanel(idx, { projectId: nextProject });
                    // If the panel's current hole isn't in the picked
                    // project, clear it so the user re-picks one that
                    // is — otherwise the hole dropdown shows empty
                    // and the chart still plots the old hole.
                    const current = traceGraphs[idx]?.config?.holeId;
                    if (nextProject && current) {
                      const hole = holeOptionsWithProject.find((h) => h.holeId === current);
                      if (hole?.project !== nextProject) handleConfigChange(idx, { holeId: '' });
                    }
                  },
                }}
                propertyOptions={graph?.propertyOptions || []}
                onConfigChange={(patch) => {
                  // The display toggles (rendered inside TracePlot) aren't
                  // part of the trace-grid hook's config — persist them in
                  // the panel cache; everything else flows to the hook.
                  const { logScale, usePatterns, stepped, fillArea, startFromZero, ...gridPatch } = patch;
                  const toggles = { logScale, usePatterns, stepped, fillArea, startFromZero };
                  const togglePatch = Object.fromEntries(
                    Object.entries(toggles).filter(([, value]) => value !== undefined)
                  );
                  if (Object.keys(togglePatch).length) setStripPanel(idx, togglePatch);
                  if (Object.keys(gridPatch).length) handleConfigChange(idx, gridPatch);
                }}
                template={template}
                colourMap={buildCategoricalColorMap(geologyHoles, rawCsv?.colors, graph?.config?.property)}
              />
            );
          })}
        </div>
      )}
      {dataSourceTarget && createPortal(dataSourceInfo, dataSourceTarget)}
      {controlsTarget && createPortal(sidebarControls, controlsTarget)}
    </div>
  );
}

export default Drillhole2D;
